import { useCallback, useEffect, useRef, type FC } from "react";
import { useForm } from "react-hook-form";
import styled from "styled-components";

import { useRootDispatch, useRootSelector } from "@ds/hooks";

import { locationsActions } from "@ds/modules/locations/redux/slice";
import {
  selectLocationById,
  selectLocations,
  selectLocationsError,
  selectLocationsIsLoading,
  selectSelectedLocationIds,
} from "@ds/modules/locations/redux/selectors";

import { Button, ButtonActionType, ButtonType } from "@ds/components/forms/buttons";
import { InputText } from "@ds/components/forms/inputs";
import { LabelWrapper } from "@ds/components/forms/labels";
import { IconType } from "@ds/components/icons";
import { Dialog } from "@ds/components/modals";

const Form = styled.form`
  display: flex;
  flex-direction: column;
  min-width: 400px;
`;

export const DuplicateLocationModal: FC<{ onClose: () => void; locationId?: number; onSuccess?: () => void }> = ({
  onClose,
  locationId: locationIdProp,
  onSuccess,
}) => {
  const selectedLocationId = useRootSelector(selectSelectedLocationIds)[0];
  const locationId = locationIdProp ?? selectedLocationId;
  const location = useRootSelector(state => selectLocationById(state, locationId));
  const locations = useRootSelector(selectLocations);
  const locationNames = locations.map(_location => _location.name.trim().toLowerCase());
  const isLoading = useRootSelector(selectLocationsIsLoading);
  const error = useRootSelector(selectLocationsError);
  const hasSubmitted = useRef(false);
  const dispatch = useRootDispatch();

  useEffect(() => {
    if (hasSubmitted.current && !isLoading) {
      if (!error) {
        onSuccess?.();
      }
      onClose();
    }
  }, [isLoading, error, onClose, onSuccess]);

  const {
    register,
    handleSubmit,
    watch,
    formState: { errors },
  } = useForm<{ name: string }>({
    defaultValues: {
      name: location?.name ? `${location.name} (Copy)` : "",
    },
  });

  const onSubmit = useCallback(
    (data: { name: string }) => {
      if (locationId === undefined || locationId === null) return;
      hasSubmitted.current = true;
      dispatch(locationsActions.duplicateLocation({ locationId, newTitle: data.name }));
    },
    [dispatch, locationId],
  );

  // NOTE: Name uniqueness is validated against the currently loaded locations in the Redux store
  // (typically one page of results). Names outside the current page are invisible to this check,
  // so duplicate names on other pages will only be caught by the API error response.
  const { ref, ...nameRegister } = register("name", {
    required: "Name is required",
    validate: value => {
      const trimmed = value?.trim() ?? "";
      if (!trimmed) return true;
      const isDuplicate = locationNames.some(n => n.trim().toLowerCase() === trimmed.toLowerCase());
      return isDuplicate ? "A location with this name already exists" : true;
    },
  });

  const renderFooter = useCallback(
    () => (
      <>
        <Button
          label="Cancel"
          ariaLabel="cancel button"
          buttonType={ButtonType.Outlined}
          actionType={ButtonActionType.Secondary}
          icon={IconType.Close}
          iconPosition="right"
          isDisabled={isLoading}
          onClick={onClose}
        />
        <Button
          label="Duplicate"
          ariaLabel="duplicate button"
          buttonType={ButtonType.Raised}
          icon={IconType.Duplicate}
          iconPosition="right"
          isLoading={isLoading}
          isDisabled={locationId === undefined || locationId === null}
          onClick={handleSubmit(onSubmit)}
        />
      </>
    ),
    [locationId, isLoading, onClose, handleSubmit, onSubmit],
  );

  return (
    <Dialog modal visible header="Duplicate Location" footer={renderFooter} onHide={onClose}>
      <Form onSubmit={handleSubmit(onSubmit)} noValidate>
        <LabelWrapper label="New Location Name" isRequired error={errors.name}>
          <InputText
            inputRef={ref}
            placeholder="Enter a new name for the duplicated location"
            {...nameRegister}
            value={watch("name") ?? ""}
          />
        </LabelWrapper>
      </Form>
    </Dialog>
  );
};
